import { FormEvent } from "react"
import { EmployeeProps } from "./employee_type"
import { SubmittalProps } from "./submittal_types"
import { RFIProps } from "./rfi_types"
import { TaskProps } from "./tasks_types"
import { TaskProgressProps } from "./progress_types"
import { ClientMinProps } from "./client_types"
import { PointOfContactMinProps } from "./point-of-contact_types"
import { InvoiceProps } from "./invoices_types"
import { ValueConditionalStylesProps } from "@/components/TableColumnSelector/Interface"

export type ProjectStatus = 
    "Active" |
    "Hold" |
    "Review" |
    "Not Started" |
    "Completed" |
    "Cancelled"

export const PROJECT_STATUS = [
    "Active",
    "Hold",
    "Review",
    "Not Started",
    "Completed",
    "Cancelled"
]

export const PROJECT_STATUS_OPTIONS = [
    {value: "Active", label: "Active"},
    {value: "Hold", label: "Hold"},
    {value: "Review", label: "Review"},
    {value: "Not Started", label: "Not Started"},
    {value: "Completed", label: "Completed"},
    {value: "Cancelled", label: "Cancelled"},
]

// Backend uses 3P, SI, NC, but frontend uses Third Party, Inspections, New Construction
export const category_map: Record<string, string> = {
    "3P": "Third Party",
    "SI": "Inspections",
    "FP": "New Construction",
}
export const PROJECT_TEMPLATE = [
    {value: "3P", label: "Third Party"},
    {value: "SI", label: "Inspections"},
    {value: "FP", label: "New Construction"},
]

export type ProjectProps = {
    project_id?: string
    project_name: string
    manager?: EmployeeProps
    city?: string
    client_name?: string
    start_date?: string
    end_date?: string
    description?: string
    status?: ProjectStatus
    folder_location?: string
    tasks?: TaskProps[],
    submittals?: SubmittalProps[],
    rfis?: RFIProps[],
    isDeadline: boolean,
    point_of_contact?: string,
    consulting_fee?: number,
    no_date_due?: boolean // Used in the Project Form

    is_sent_to_quickbooks?: boolean // Typically altered by the Invoices
    payment_amount?: number // The invoice amount from the latest invoice
    latest_invoice?: InvoiceProps

    follow_up_date?: string

    client?: ClientMinProps
    poc?: PointOfContactMinProps

    task_progress?: TaskProgressProps

    project_progress?: number
    total_quests?: number // The total amount of tasks, submittals, and RFIs for the project

    project_size?: number

    task_counts?: OverviewProps // This is used to show the task counts on the project row. Data retreived from the pagination API
    submittal_counts?: OverviewProps // This is used to show the submittal counts on the project row
    similar_projects?: SimilarProjectsProps[] // This is used to show the similar projects on the project row. Data retreived from the pagination API

    template?: string // Uses the PROJECT_TEMPLATE array

    checklist_items: ChecklistItemCompletionProps
}

export type ChecklistItemProps = {
    completed: boolean
    completed_date: string
    completed_by: string
}

export type ChecklistItemCompletionProps = {
    SUBMITTAL_CHECKLIST: ChecklistItemProps
    DIG_TESS: ChecklistItemProps
    ISO: ChecklistItemProps
    THRID_PARTY_REVIEW: ChecklistItemProps
    FLOW_TEST: ChecklistItemProps
    AG_HYDRO: ChecklistItemProps
    UG_HYDRO: ChecklistItemProps
    FDC_CAPS: ChecklistItemProps
    FINAL: ChecklistItemProps
    CLOSEOUTS: ChecklistItemProps
}

type DateOnly = string

// This is used for creating (as well as updating) a project
export type ProjectFormCreationProps = {
    project_id?: string
    project_name: string
    manager: string // the email of the manager
    city: string
    client_name: string
    start_date: DateOnly
    end_date?: DateOnly
    description: string
    status: ProjectStatus
    template: string
    point_of_contact: string
    isDeadline: boolean
    no_date_due?: boolean
    project_size?: number
    consulting_fee?: number
    folder_location?: string
    follow_up_date?: string
}

export type ProjectFilterProps = {
    filterText: string
    onFilter: (e: FormEvent<HTMLInputElement>) => void
    onClear: () => void
}

type OverviewProps = {
    total: number
    completed: number
    category_counts: CategorySummaryProps
}

// This is used to show the category counts on the project row
// CategoryCountsProps shows the total and completed counts for each category and the unique assignees
type CategorySummaryProps = {
    Mechanical: CategoryCountsProps
    Electrical: CategoryCountsProps
    Plumbing: CategoryCountsProps
    "Fire Protection": CategoryCountsProps
    Other: CategoryCountsProps
}

type CategoryCountsProps = {
    total: number
    completed: number
    unique_assignees: string[] // This is used to show the unique assignees on the project row
}

// Smaller version of the ProjectProps that is used to show the similar projects on the project row. Data retreived from the pagination API
type SimilarProjectsProps = {
    project_id: string
    project_name: string
    template? : string
    client_name?: string
    city?: string
    status?: ProjectStatus
    folder_location?: string
}

// Value-based conditional styles for SelectorOptions component
export const PROJECT_STATUS_VALUE_CONDITIONAL_STYLES: ValueConditionalStylesProps[] = [
    {
        when: (value) => value === "Active",
        style: {
            backgroundColor: "var(--status-active)"
        }
    },
    {
        when: (value) => value === "Hold",
        style: {
            backgroundColor: "var(--status-hold)"
        }
    },
    {
        when: (value) => value === "Review",
        style: {
            backgroundColor: "var(--status-review)"
        }
    },
    {
        when: (value) => value === "Completed",
        style: {
            backgroundColor: "var(--status-completed)"
        }
    },
    {
        when: (value) => value === "Cancelled",
        style: {
            backgroundColor: "var(--status-cancelled)"
        }
    }
]